import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { signInWithGoogle, signInWithApple } from '@/lib/social-auth';
import { recordSignupConsent, type SignupConsent } from '@/services/consentService';
import { getAdditionalUserInfo, type UserCredential } from 'firebase/auth';

/** 소셜 최초 로그인(=가입)일 때만 동의를 기록한다. 재로그인은 이미 기록이 있다. */
async function recordConsentIfNewUser(
  credential: UserCredential,
  consent: SignupConsent | undefined
): Promise<void> {
  if (!consent) return;
  if (!getAdditionalUserInfo(credential)?.isNewUser) return;
  await recordSignupConsent(credential.user.uid, consent);
}
import { AuthContext } from './auth-context';
import type { AuthContextType } from './auth-context';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 10초 타임아웃: iOS WebView에서 onAuthStateChanged 콜백이 미응답 시 무한 로딩 방지
    const timeoutId = setTimeout(() => {
      logger.error('onAuthStateChanged timeout — forcing loading false');
      setLoading(false);
    }, 10000);

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        clearTimeout(timeoutId);
        setUser(user);
        setLoading(false);
      },
      (error) => {
        clearTimeout(timeoutId);
        logger.error('onAuthStateChanged error', error);
        setLoading(false);
      },
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const signup = async (
    email: string,
    password: string,
    displayName?: string,
    consent?: SignupConsent
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      if (displayName && userCredential.user) {
        await updateProfile(userCredential.user, { displayName });
      }

      // 동의 기록 (개인정보보호법 §22 입증 책임)
      if (consent && userCredential.user) {
        await recordSignupConsent(userCredential.user.uid, consent);
      }
    } catch (error) {
      logger.error('Signup error:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      logger.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      logger.error('Password reset error:', error);
      throw error;
    }
  };

  /**
   * 소셜 로그인. 최초 로그인은 곧 가입이므로, 신규 계정이면 동의 기록을 남긴다
   * (개인정보보호법 §22 입증 책임 — 체크박스로 버튼을 막는 것만으로는 기록이 없다).
   */
  const loginWithGoogle = async (consent?: SignupConsent) => {
    try {
      const credential = await signInWithGoogle();
      await recordConsentIfNewUser(credential, consent);
    } catch (error) {
      if (error instanceof Error && error.message === 'redirect') return;
      logger.error('Google login error:', error);
      throw error;
    }
  };

  const loginWithApple = async (consent?: SignupConsent) => {
    try {
      const credential = await signInWithApple();
      await recordConsentIfNewUser(credential, consent);
    } catch (error) {
      logger.error('Apple login error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
